import type { ReactNode } from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import type { FieldRow } from './content';

type Props = {
  /** Accessible name — also how tests address the table. */
  label: string;
  /** Column headers; pass two when the rows have no type column. */
  headers: readonly string[];
  rows: readonly FieldRow[];
};

/** Renders `code spans` in a description without pulling in a markdown lib. */
export function inlineCode(text: string): ReactNode[] {
  return text
    .split('`')
    .map((part, i) => (i % 2 === 1 ? <code key={i}>{part}</code> : part));
}

const mono = {
  fontFamily: 'IBM Plex Mono',
  fontSize: '0.8125rem',
  verticalAlign: 'top',
} as const;

export function FieldTable({ label, headers, rows }: Props) {
  const hasType = rows.some((row) => row.type !== undefined);
  return (
    <Box sx={{ overflowX: 'auto', my: 2, border: '1px solid var(--rule)', borderRadius: 0.5 }}>
      <Table size="small" aria-label={label}>
        <TableHead>
          <TableRow>
            {headers.map((header) => (
              <TableCell
                key={header}
                sx={{
                  fontFamily: 'IBM Plex Mono',
                  fontSize: '0.6875rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  color: 'var(--ink-muted)',
                  borderBottom: '1px solid var(--rule)',
                  backgroundColor: 'var(--bg-elevated)',
                }}
              >
                {header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.name} sx={{ '&:last-child td': { borderBottom: 0 } }}>
              <TableCell sx={{ ...mono, whiteSpace: 'nowrap' }}>{row.name}</TableCell>
              {hasType && (
                <TableCell sx={{ ...mono, color: 'var(--ink-muted)', whiteSpace: 'nowrap' }}>
                  {row.type}
                </TableCell>
              )}
              <TableCell
                sx={{
                  fontSize: '0.875rem',
                  lineHeight: 1.55,
                  color: 'var(--ink-muted)',
                  verticalAlign: 'top',
                  minWidth: 240,
                  overflowWrap: 'anywhere',
                }}
              >
                {inlineCode(row.description)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}
